import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../Utils/baseQueryWithReauth";
import { ProductTelemetryMonthSnapshot } from "../components/CustomerView/CustomerReportPdf";
import {
  IPartnerEngagementsResponse,
  IPartnerFavoriteProps,
  IPartnerProfileResponse,
  IPartnerSubscriptionDataResponse,
  IPartnerTenantDetailsResponse,
  ISelectedPartnerInformation,
} from "../interfaces/PartnerView.interfaces";
import { ContactDataGroup, IContactData, IPartnerContacts } from "../SharedComponents/Interfaces/CustomContact.interfaces";
import { transformContactListResponse } from "./ExploreCustomers.slice";

// Define a service using a base URL and expected endpoints
export const explorePartnersAPI = createApi({
  reducerPath: "explorePartners",
  tagTypes: ["FavoritePartners"],
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getPartnersList: builder.query({
      query: () => "partner/getPartners"
    }),
    getPartnerInformationData: builder.mutation<
      ISelectedPartnerInformation,
      { partnerOneID: string | null; mpnIdentifier?: string }
    >({
      query: ({ partnerOneID, mpnIdentifier }) =>
        `partner/getPartnerInformationData?partnerOneID=${partnerOneID}&mpnIdentifier=${mpnIdentifier}`
    }),
    getPartnersAIInsightData: builder.query<
      string,
      { componentName: string; partnerOneID?: string; partnerOneName?: string }
    >({
      query: ({ componentName, partnerOneID, partnerOneName }) => ({
        url: `partner/getPartnerAIInsightData?componentName=${componentName}&partnerOneID=${partnerOneID}&partnerOneName=${partnerOneName}`,
        // This is the same as passing 'text'
        responseHandler: (response) => response.text()
      })
    }),
    getPartnerProfilePageData: builder.mutation<
      IPartnerProfileResponse,
      { partnerOneID: string | null; mpnIdentifier: string; workloadIdentifier: string }
    >({
      query: ({ partnerOneID, mpnIdentifier, workloadIdentifier }) =>
        `partner/getPartnerProfilePageData?partnerOneID=${partnerOneID}&mpnIdentifier=${mpnIdentifier}&workloadIdentifier=${workloadIdentifier}`
    }),
    getPartnerTenantsPageData: builder.mutation<
      IPartnerTenantDetailsResponse,
      { partnerOneID: string | null; mpnIdentifier: string; workloadIdentifier: string }
    >({
      query: ({ partnerOneID, mpnIdentifier, workloadIdentifier }) =>
        `partner/getPartnerTenantsPageData?partnerOneID=${partnerOneID}&mpnIdentifier=${mpnIdentifier}&workloadIdentifier=${workloadIdentifier}`
    }),
    getPartnerEngagementData: builder.query<
      IPartnerEngagementsResponse,
      { partnerOneID: string | null; mpnIdentifier: string }
    >({
      query: ({ partnerOneID, mpnIdentifier }) =>
        `partner/getPartnerEngagementData?partnerOneID=${partnerOneID}&mpnIdentifier=${mpnIdentifier}`
    }),
    getPartnerContactList: builder.query({
      query: ({ partnerOneID, mpnID }) => `partner/getPartnerContactList?partnerOneID=${partnerOneID}&mpnID=${mpnID}`,
      transformResponse: async (response: { partnerContacts: any; cxeContacts: any }) => ({
        partnerContacts: await transformContactListResponse<IPartnerContacts, ContactDataGroup>(
          response?.partnerContacts,
          "subsidiary"
        ),
        cxeContacts: await transformContactListResponse<IContactData, ContactDataGroup>(
          response?.cxeContacts,
          "productName"
        )
      })
    }),
    insertOrUpdateFavoritePartners: builder.mutation<
      boolean,
      { partnerOneId: string; partnerOneName: string; loginName: string; loginEmail: string; isFavorite: boolean }
    >({
      query: ({ partnerOneId, partnerOneName, loginName, loginEmail, isFavorite }) => ({
        url: "partner/insertOrUpdateFavoritePartners",
        method: "POST",
        body: { partnerOneId, partnerOneName, loginName, loginEmail, isFavorite }
      }),
      invalidatesTags: ["FavoritePartners"]
    }),
    getFavoritePartnersByLogin: builder.query<IPartnerFavoriteProps[], void>({
      query: () => "partner/getFavoritePartners",
      providesTags: ["FavoritePartners"]
    }),
    getPartnerSubscriptionData: builder.mutation<
      IPartnerSubscriptionDataResponse,
      { partnerOneID: string | null; mpnIdentifier: string; workloadIdentifier: string }
    >({
      query: ({ partnerOneID, mpnIdentifier, workloadIdentifier }) =>
        `partner/getPartnerSubscriptionData?partnerOneID=${partnerOneID}&mpnIdentifier=${mpnIdentifier}&workloadIdentifier=${workloadIdentifier}`
    }),
    getPartnerProductTelemetryData: builder.query<
      ProductTelemetryMonthSnapshot[],
      { partnerOneID: string | null; mpnIdentifier: string; partnerType: string }
    >({
      query: ({ partnerOneID, mpnIdentifier, partnerType }) =>
        `partner/getPartnerProductTelemetryData?partnerOneID=${partnerOneID}&mpnIdentifier=${mpnIdentifier}&partnerType=${partnerType}`
    }),
    getPartnerFeaturesData: builder.query({
      query: ({ partnerOneID, mpnIdentifier, productFamily }: { partnerOneID: any; mpnIdentifier: any; productFamily: any }) =>
        `partner/getPartnerFeatureData?partnerOneID=${partnerOneID}&mpnIdentifier=${mpnIdentifier}&productFamily=${productFamily}`
    })
  })
});

// Export hooks for usage in functional components, which are
// Auto-generated based on the defined endpoints
export const {
  useGetPartnersListQuery,
  useGetPartnerInformationDataMutation,
  useLazyGetPartnersAIInsightDataQuery,
  useGetPartnerProfilePageDataMutation,
  useGetPartnerSubscriptionDataMutation,
  useGetPartnerTenantsPageDataMutation,
  useLazyGetPartnerEngagementDataQuery,
  useGetPartnerContactListQuery,
  useInsertOrUpdateFavoritePartnersMutation,
  useGetFavoritePartnersByLoginQuery,
  useGetPartnerProductTelemetryDataQuery,
  useGetPartnerFeaturesDataQuery
} = explorePartnersAPI;
